import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface SelectionModalProps<T> {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  title?: string;
  children: React.ReactNode;
}

// Generic Selection Modal
export const SelectionModal = <T,>({
  open,
  onClose,
  onSave,
  title = "Browse",
  children
}: SelectionModalProps<T>) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          borderRadius: '8px',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex',
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 'medium' }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Grid sx={{ height: '60vh', overflow: 'auto' }}>
          {children}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={onSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ChoiceModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onCreateNew: () => void;
  onAddExisting: () => void;
  createNewText: string;
  addExistingText: string;
}

// Choice Modal Component
export const ChoiceModal = ({
  open,
  onClose,
  title,
  onCreateNew,
  onAddExisting,
  createNewText,
  addExistingText
}: ChoiceModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '24px',
          backgroundColor: '#f5f5f5'
        }
      }}
    >
      <DialogContent sx={{ p: 0, textAlign: 'center' }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            mb: 4, 
            fontWeight: 'bold',
            color: '#333',
            lineHeight: 1.4
          }}
        >
          {title.split(' ').slice(0, 3).join(' ')}<br />
          {title.split(' ').slice(3).join(' ')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
          <Button
            onClick={onCreateNew}
            variant="contained"
            sx={{
              bgcolor: 'black',
              color: 'white',
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 'medium',
              px: 2,
              py: 1,
              '&:hover': {
                bgcolor: '#333'
              }
            }}
          >
            {createNewText}
          </Button>
          
          <Button
            onClick={onAddExisting}
            variant="outlined"
            sx={{
              borderColor: '#ccc',
              color: '#333',
              backgroundColor: 'white',
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 'medium',
              px: 2,
              py: 1,
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f9f9f9'
              }
            }}
          >
            {addExistingText}
          </Button>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'flex-end', p: 0, mt: 3 }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            color: '#666',
            textTransform: 'none',
            fontWeight: 'medium'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};